import React, { useCallback, useState } from 'react';

import { useTranslate } from '@im/react-localization';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  ActionsContainer,
  StyledModalContent,
  StyledDialog,
  StyledModalDialogContent,
} from 'src/components/JsonViewer/common/CommonStyles';
import { getResolutionHeight } from 'src/components/JsonViewer/utils/common.utils';
import { useCopyToClipboard } from 'src/components/JsonViewer/hooks/use-copytoclipboard';
import GeneralSkeleton from 'src/components/JsonViewer/common/GeneralSkeleton';
import { NoData } from '@im/ui-lib/icons/NoData';
import SnackBarNotification from 'src/components/SnackBar/SnackBarNotification';

interface JsonViewerDialogProps {
  handleClose: () => void;
  heading: string;
  isError?: boolean;
  isLoading?: boolean;
  json?: object;
  onCopy?: (json?: object) => void;
  open: boolean;
}
export default function JsonViewerDialog(
  props: Readonly<JsonViewerDialogProps>
) {
  const [showToast, setShowToast] = useState(false);
  const [, copy] = useCopyToClipboard();
  const translate = useTranslate();

  const onCopy = useCallback(() => {
    props.onCopy?.(props.json);
    if (props.json) {
      copy(JSON.stringify(props.json))
        .then(() => {
          setShowToast(true);
        })
        .catch((error: any) =>
          console.error('Error occurred in Bids copyBidDetails()', error)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copy, props.json]);

  return (
    <StyledDialog
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      aria-describedby="bidstatus-details-dialog"
      closeAfterTransition
      data-name="bidstatus-details-dialog"
      fullWidth
      maxWidth={getResolutionHeight() <= 768 ? 'md' : 'lg'}
      onClose={props.handleClose}
      open={props.open}
      scroll="body"
    >
      <StyledModalDialogContent>
        {showToast && (
          <SnackBarNotification
            Variant={'Success'}
            message={translate('Common.CopiedToClipboard')}
          />
        )}
        <Stack spacing={2}>
          <Stack
            direction={'row'}
            id="bidstatus-details-dialog-title"
            justifyContent="space-between"
          >
            <Typography variant="h1">{props.heading}</Typography>
            <IconButton
              aria-label="close button"
              data-name="dialog-bids-widget-xout"
              onClick={props.handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <>
            {(!props.json || props.isError) && !props.isLoading && (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={3}
                sx={{ minHeight: '40vh' }}
              >
                <NoData />
                <Typography variant="bodycopysemibold">
                  {translate('Common.NoRecord')}
                </Typography>
              </Stack>
            )}
            {props.json && !props.isLoading && !props.isError && (
              <Stack spacing={2}>
                <StyledModalContent>
                  <Box
                    sx={{
                      backgroundColor: (theme) => theme?.palette?.neutral?.[50],
                      padding: '16px',
                      width: 'fit-content',
                    }}
                  >
                    <code>
                      <pre
                        style={{
                          margin: 0,
                          maxHeight: '90vh !important',
                        }}
                      >
                        {JSON.stringify(props.json, null, 4)}
                      </pre>
                    </code>
                  </Box>
                </StyledModalContent>
              </Stack>
            )}
            {!props.json && props.isLoading && !props.isError && (
              <GeneralSkeleton />
            )}
          </>
        </Stack>
      </StyledModalDialogContent>
      <DialogActions>
        <ActionsContainer component="div">
          {props.json && !props.isLoading && !props.isError && (
            <Button
              data-testid="copy-bidstatus-details-button"
              onClick={onCopy}
              size="medium"
              sx={{ color: '#0164B0', textTransform: 'none' }}
              variant="newOutlined"
            >
              <Typography variant="smalltextsemibold">
                {translate('Common.Copy')}
              </Typography>
            </Button>
          )}
        </ActionsContainer>
      </DialogActions>
    </StyledDialog>
  );
}
